import { Controller } from "@hotwired/stimulus"
import { exifr, parseExifLocation } from "../lib/exif"
import { canvasResize, PHOTO_THUMBNAIL } from "../lib/image_resize"
import { postJson, post } from "../lib/http"

export default class extends Controller {
  static targets = [
    "fileInput", "dropzone", "progress", "progressBar", "progressText",
    "stats", "totalCount", "gpsCount", "noGpsCount", "waitingSection",
    "zeroGpsError", "gpsUploadedCount"
  ]

  static values = {
    uploadUrl: String
  }

  connect() {
    this.files = []
    this.metadata = []
    this.processed = 0
    this.withGps = 0
    this.withoutGps = 0
    this.cumulativeTotal = 0
    this.cumulativeWithGps = 0
    this.cumulativeWithoutGps = 0
    this.totalGpsUploaded = 0
    window.photoFileMap = window.photoFileMap || {}
  }

  selectFiles() {
    this.fileInputTarget.click()
  }

  dragover(event) {
    event.preventDefault()
    this.dropzoneTarget.classList.add("border-indigo-400", "bg-indigo-50")
  }

  dragleave(event) {
    event.preventDefault()
    this.dropzoneTarget.classList.remove("border-indigo-400", "bg-indigo-50")
  }

  drop(event) {
    event.preventDefault()
    this.dropzoneTarget.classList.remove("border-indigo-400", "bg-indigo-50")
    const files = Array.from(event.dataTransfer.files).filter(f => f.type.startsWith("image/"))
    if (files.length > 0) {
      this.processFiles(files)
    }
  }

  filesSelected(event) {
    const files = Array.from(event.target.files)
    if (files.length > 0) {
      this.processFiles(files)
    }
  }

  async processFiles(files) {
    this.files = files
    this.processed = 0
    this.withGps = 0
    this.withoutGps = 0
    this.metadata = []

    if (this.hasProgressTarget) this.progressTarget.classList.remove("hidden")
    if (this.hasStatsTarget) this.statsTarget.classList.remove("hidden")
    if (this.hasWaitingSectionTarget) this.waitingSectionTarget.classList.add("hidden")
    if (this.hasZeroGpsErrorTarget) this.zeroGpsErrorTarget.classList.add("hidden")
    if (this.hasDropzoneTarget) this.dropzoneTarget.classList.add("hidden")

    if (this.hasTotalCountTarget) this.totalCountTarget.textContent = this.cumulativeTotal + files.length

    // Modest batch — this web path sends base64 thumbnails INLINE, so big batches
    // would mean multi-MB request bodies. The server now bulk-upserts each batch.
    const BATCH_SIZE = 40
    let batch = []

    for (let i = 0; i < files.length; i++) {
      const file = files[i]
      try {
        const loc = await parseExifLocation(file)
        if (loc) {
          const clientRef = await this.computeClientRef(file)
          const thumbnail = await this.generateThumbnail(file)
          const entry = {
            latitude: loc.latitude,
            longitude: loc.longitude,
            taken_on: loc.takenOn,
            taken_at: loc.takenAt,
            client_ref: clientRef,
            thumbnail: thumbnail
          }
          this.metadata.push(entry)
          batch.push(entry)
          this.withGps++

          window.photoFileMap[clientRef] = file
        } else {
          this.withoutGps++
        }
      } catch (e) {
        console.warn("EXIF parse failed for", file.name, e)
        this.withoutGps++
      }

      this.processed++
      this.updateProgress()

      if (batch.length >= BATCH_SIZE) {
        await this.sendBatch(batch)
        batch = []
      }
    }

    if (batch.length > 0) {
      await this.sendBatch(batch)
    }

    if (this.withGps === 0) {
      // No GPS photos — show error and let them try again
      if (this.hasZeroGpsErrorTarget) this.zeroGpsErrorTarget.classList.remove("hidden")
      if (this.hasDropzoneTarget) this.dropzoneTarget.classList.remove("hidden")
      if (this.hasProgressTarget) this.progressTarget.classList.add("hidden")
    } else {
      // Finalize the upload so the server knows this participant is done and can
      // start matching once the other participant is also ready.
      await this.finalize()
      // Accumulate totals across multiple selections
      this.cumulativeTotal += files.length
      this.cumulativeWithGps += this.withGps
      this.cumulativeWithoutGps += this.withoutGps
      this.totalGpsUploaded += this.withGps
      // Update stats boxes to show cumulative counts
      if (this.hasTotalCountTarget) this.totalCountTarget.textContent = this.cumulativeTotal
      if (this.hasGpsCountTarget) this.gpsCountTarget.textContent = this.cumulativeWithGps
      if (this.hasNoGpsCountTarget) this.noGpsCountTarget.textContent = this.cumulativeWithoutGps
      if (this.hasGpsUploadedCountTarget) this.gpsUploadedCountTarget.textContent = this.totalGpsUploaded
      if (this.hasWaitingSectionTarget) this.waitingSectionTarget.classList.remove("hidden")
    }
  }

  addMorePhotos() {
    if (this.hasDropzoneTarget) this.dropzoneTarget.classList.remove("hidden")
    if (this.hasWaitingSectionTarget) this.waitingSectionTarget.classList.add("hidden")
    if (this.hasZeroGpsErrorTarget) this.zeroGpsErrorTarget.classList.add("hidden")
    if (this.hasProgressTarget) this.progressTarget.classList.add("hidden")
    if (this.hasStatsTarget) this.statsTarget.classList.add("hidden")
    // Go straight to file picker
    this.selectFiles()
  }

  updateProgress() {
    const pct = Math.round((this.processed / this.files.length) * 100)
    if (this.hasProgressBarTarget) this.progressBarTarget.style.width = `${pct}%`
    if (this.hasProgressTextTarget) this.progressTextTarget.textContent = `${this.processed}/${this.files.length}`
    // Show cumulative + in-progress counts during processing
    if (this.hasGpsCountTarget) this.gpsCountTarget.textContent = this.cumulativeWithGps + this.withGps
    if (this.hasNoGpsCountTarget) this.noGpsCountTarget.textContent = this.cumulativeWithoutGps + this.withoutGps
  }

  async computeClientRef(file) {
    const str = `${file.name}|${file.size}|${file.lastModified}`

    // crypto.subtle is only available in secure contexts (HTTPS or localhost)
    // Fall back to a simple hash for HTTP connections
    if (crypto.subtle) {
      const encoder = new TextEncoder()
      const data = encoder.encode(str)
      const hashBuffer = await crypto.subtle.digest("SHA-256", data)
      const hashArray = Array.from(new Uint8Array(hashBuffer))
      return hashArray.map(b => b.toString(16).padStart(2, "0")).join("")
    } else {
      // Simple string hash fallback
      let hash = 0
      for (let i = 0; i < str.length; i++) {
        const char = str.charCodeAt(i)
        hash = ((hash << 5) - hash) + char
        hash |= 0
      }
      return "ref-" + Math.abs(hash).toString(16) + "-" + file.size + "-" + file.lastModified
    }
  }

  async generateThumbnail(file) {
    // Primary: canvas — works for JPEG, PNG, WebP, and HEIC on Safari/iOS.
    const canvasResult = await canvasResize(file, PHOTO_THUMBNAIL)
    if (canvasResult) return canvasResult

    // Fallback: extract embedded JPEG thumbnail via exifr (covers some HEIC files).
    try {
      const thumbBuffer = await exifr.thumbnail(file)
      if (thumbBuffer) {
        const blob = new Blob([thumbBuffer], { type: "image/jpeg" })
        return await new Promise((resolve) => {
          const reader = new FileReader()
          reader.onload = (e) => resolve(e.target.result)
          reader.onerror = () => resolve(null)
          reader.readAsDataURL(blob)
        })
      }
    } catch (e) {
      // No embedded thumbnail available
    }

    // No thumbnail could be generated (e.g. HEIC on Chrome desktop — no native codec).
    return null
  }

  async finalize() {
    await post(this.uploadUrlValue.replace("/batch_create", "/finalize"))
  }

  async sendBatch(batch) {
    await postJson(this.uploadUrlValue, { metadata: batch })
  }

}
